/**
 * Manabun認証フック
 *
 * Manabunからの認証情報を取得・管理するカスタムフック
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  ManabunUserResponse,
  ManabunAuthStatus,
  ManabunAuthError,
} from '../types/manabunUser';
import {
  authenticateManabunUser,
  isTrialUser,
  hasCourseAccess,
  getUserType,
  type UserType,
} from '../api/manabunAuth';

interface UseManabunAuthResult {
  /** ユーザー情報 */
  user: ManabunUserResponse | null;
  /** 認証状態 */
  status: ManabunAuthStatus;
  /** エラー情報 */
  error: ManabunAuthError | null;
  /** ユーザー種別（staff / trial / regular） */
  userType: UserType | null;
  /** 認証を再実行 */
  retry: () => void;
  /** ログアウト（セッションクリア） */
  logout: () => void;
  /** 試用ユーザーかどうか（講座指定） */
  isTrial: (kozaId: number) => boolean;
  /** 講座・年・月へのアクセス権があるか */
  canAccessCourse: (courseId: number, yearMonths: number[]) => boolean;
}

/**
 * Manabun認証フック
 */
export function useManabunAuth(tenantSlug: string | undefined): UseManabunAuthResult {
  const [user, setUser] = useState<ManabunUserResponse | null>(null);
  const [status, setStatus] = useState<ManabunAuthStatus>('idle');
  const [error, setError] = useState<ManabunAuthError | null>(null);

  const authenticate = useCallback(async () => {
    if (!tenantSlug) return;

    setStatus('loading');
    setError(null);

    try {
      const userData = await authenticateManabunUser(tenantSlug);
      setUser(userData);
      setStatus('authenticated');
    } catch (err) {
      const authError = err as ManabunAuthError;
      setError(authError);
      setStatus('error');
      console.error('[useManabunAuth] 認証エラー:', authError);
    }
  }, [tenantSlug]);

  const retry = useCallback(() => {
    setUser(null);
    authenticate();
  }, [authenticate]);

  const logout = useCallback(() => {
    setUser(null);
    setStatus('idle');
    setError(null);
  }, []);

  const isTrial = useCallback(
    (kozaId: number): boolean => {
      if (!user) return false;
      return isTrialUser(user, kozaId);
    },
    [user]
  );

  const canAccessCourse = useCallback(
    (courseId: number, yearMonths: number[]): boolean => {
      if (!user) return false;
      return hasCourseAccess(user, courseId, yearMonths);
    },
    [user]
  );

  const userType = useMemo<UserType | null>(
    () => (user ? getUserType(user) : null),
    [user]
  );

  useEffect(() => {
    // tenantSlug が確定し、かつ未認証の場合に認証を実行
    if (status === 'idle' && tenantSlug) {
      authenticate();
    }
  }, [status, tenantSlug, authenticate]);

  return {
    user,
    status,
    error,
    userType,
    retry,
    logout,
    isTrial,
    canAccessCourse,
  };
}
